import * as React from "react";
import Box from "@mui/material/Box";
import {
  DataGrid,
  GridColDef,
  GridRowModel,
  GridRenderCellParams,
  GridActionsCellItem,
} from "@mui/x-data-grid";
import { useMutation, useQuery } from "@tanstack/react-query";
import { API_URL, BootstrapTooltip, handleSendMessage } from "src/utilities";
import { useAuth } from "src/context/auth";
import { IAutoCompleteLabelValue } from "src/interface/enum/GeneralType";
import renderCellExpand from "src/GridComponents/GridExpand";
import moment from "jalali-moment";
import {
  ListOfStatusGlobal,
  StatusGlobal,
} from "src/interface/enum/StatusGlobal";
import { Chip } from "@mui/material";
import { IAppUser } from "src/interface/IUser";
import { TypeUser } from "src/interface/enum/TypeUser";
import { IFollowupSchedule } from "src/interface/IFollowupSchedule";
import ManageHistoryIcon from "@mui/icons-material/ManageHistory";
import ChangeStatus from "src/components/status-log/ChangeStatus";
import { IFollowUpGroups } from "src/interface/IFollowUpGroups";
import SaveIcon from "@mui/icons-material/Save";
import { useNotifications } from "@toolpad/core/useNotifications";

export default function FollowupChilds({
  rows,
  setRows,
  refetch,
}: {
  rows: IFollowupSchedule[];
  setRows: (rows: IFollowupSchedule[]) => void;
  refetch: any;
}) {
  const processRowUpdate = (newRow: GridRowModel) => {
    const updatedRow: IFollowupSchedule = { ...newRow, isNew: false }; // علامت‌گذاری ردیف به عنوان "نه جدید"
    setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
    return updatedRow;
  };
  const { user } = useAuth();

  const { data: EditorsData } = useQuery({
    queryKey: ["EditorsData"],
    queryFn: () =>
      fetch(API_URL + "user/getByRole/" + TypeUser.EDITOR, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${user?.token}`,
          Aceept: "application/json",
        },
      }).then((res) => res.json()),
  });
  const [getEditors, setEditors] = React.useState<IAutoCompleteLabelValue[]>();
  React.useEffect(() => {
    if (EditorsData) {
      setEditors(
        EditorsData.map((item: IAppUser) => ({
          label: item.fName + " " + item.lName,
          value: item.id,
        }))
      );
    }
  }, [EditorsData]);

  const { data: FollowupData } = useQuery({
    queryKey: ["FollowupData"],
    queryFn: () =>
      fetch(API_URL + "followup-groups", {
        method: "GET",
        headers: {
          Authorization: `Bearer ${user?.token}`,
          Aceept: "application/json",
        },
      }).then((res) => res.json()),
  });
  const [getFollowup, setFollowup] =
    React.useState<IAutoCompleteLabelValue[]>();
  React.useEffect(() => {
    if (FollowupData) {
      setFollowup(
        FollowupData.map((item: IFollowUpGroups) => ({
          label: item.title,
          value: item.id,
        }))
      );
    }
  }, [FollowupData]);

  const columns: GridColDef[] = [
    {
      field: "actions",
      type: "actions",
      headerAlign: "center",
      headerName: "عملیات",
      align: "center",
      sortable: false,
      disableColumnMenu: true,
      cellClassName: "actions",
      minWidth: 150,
      getActions: (params) => {
        if (params.row.groupRef !== undefined) {
          return [
            <BootstrapTooltip title="تاریخچه تغییر وضعیت">
              <GridActionsCellItem
                icon={<ManageHistoryIcon color="warning" />}
                label="تاریخچه تغییر وضعیت"
                className="textPrimary"
                onClick={handleSetHistoryClick(params.row)}
                color="inherit"
              />
            </BootstrapTooltip>,
            <BootstrapTooltip title="ذخیره">
              <GridActionsCellItem
                icon={<SaveIcon color="success" />}
                label="ذخیره"
                className="textPrimary"
                onClick={handleSaveClick(params.row)}
                color="inherit"
              />
            </BootstrapTooltip>,
          ];
        }
        return [
          <BootstrapTooltip title="تاریخچه تغییر وضعیت">
            <GridActionsCellItem
              icon={<ManageHistoryIcon color="warning" />}
              label="تاریخچه تغییر وضعیت"
              className="textPrimary"
              onClick={handleSetHistoryClick(params.row)}
              color="inherit"
            />
          </BootstrapTooltip>,
        ];
      },
    },
    {
      field: "title",
      headerName: "عنوان",
      align: "left",
      headerAlign: "left",
      editable: true,
      sortable: true,
      minWidth: 180,
    },
    {
      field: "lastTaskLog",
      headerName: "وضعیت",
      align: "center",
      headerAlign: "center",
      editable: false,
      sortable: true,
      // type: "singleSelect",
      // valueOptions: ListOfStatusGlobal,
      renderCell: (params) => (
        <Chip
          label={
            ListOfStatusGlobal.find(
              (g) =>
                g.value ===
                (params.value
                  ? params.value?.statusTask
                  : StatusGlobal.NotStarted)
            )?.label
          }
          //@ts-ignore
          color={
            ListOfStatusGlobal.find(
              (g) =>
                g.value ===
                (params.value
                  ? params.value?.statusTask
                  : StatusGlobal.NotStarted)
            )?.color
          }
          variant="filled"
        />
      ),
      minWidth: 150,
    },

    {
      field: "dueOn",
      headerName: "تاریخ سر رسید",
      align: "center",
      headerAlign: "center",
      editable: false,
      sortable: true,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<any, string>) => {
        if (params.value && params.value !== "") {
          let date = params?.value;

          // اطمینان از اینکه date یک رشته است
          if (
            typeof date === "string" &&
            (date === "Invalid" || date.startsWith("InvalidTDate"))
          ) {
            return "-";
          }

          // در غیر این صورت، تاریخ را به جلالی تبدیل می‌کنیم
          return moment(date).format("jYYYY/jMM/jDD");
        } else {
          return "-";
        }
      },
    },
    {
      field: "creatorRef",
      headerName: "کارمند مرتبط",
      align: "left",
      headerAlign: "left",
      editable: true,
      sortable: true,
      minWidth: 180,
      type: "singleSelect",
      valueOptions: getEditors || [],
    },
    {
      field: "groupRef",
      headerName: "گروه پیگیری",
      align: "left",
      headerAlign: "left",
      editable: false,
      sortable: true,
      minWidth: 280,
      type: "singleSelect",
      valueOptions: getFollowup,
    },
    {
      field: "desc",
      headerName: "توضیحات ثابت",
      align: "center",
      headerAlign: "center",
      editable: true,
      sortable: true,
      minWidth: 250,
      renderCell: renderCellExpand,
    },
    {
      field: "childDesc",
      headerName: "توضیحات پیگیری",
      align: "center",
      headerAlign: "center",
      editable: true,
      sortable: true,
      minWidth: 250,
      renderCell: renderCellExpand,
    },
  ];
  const [openChangeTask, setOpenChangeTask] = React.useState<boolean>(false);
  const [rowInfo, setRowInfo] = React.useState<IFollowupSchedule>();
  const handleSetHistoryClick = (row: any) => () => {
    setOpenChangeTask(true);
    setRowInfo(row);
  };
  const notifications = useNotifications();
  const { mutateAsync: updateAsync, isPending: isEditLoading } = useMutation({
    mutationFn: async (body: any) => {
      const response = await fetch(API_URL + "followup-schedule/" + body.id, {
        method: "PUT",
        headers: {
          Accept: "*/*",
          "Content-Type": "application/json-patch+json",
          Authorization: `Bearer ${user?.token}`,
        },
        body: JSON.stringify(body),
      });
      return await response.json();
    },
    onSuccess: (responseData) => {
      JSON.stringify(responseData);
    },
    onError: (error) => {
      handleSendMessage(
        notifications,
        "خطا در دریافت اطلاعات" + error,
        "error"
      );

      console.log("eee", error);
    },
  });
  const handleSaveClick = (row: any) => async () => {
    console.log("🚀 ~ row:", row);
    //setOpenChangeTask(true);
    const res = await updateAsync({ ...row, id: row.id });
    if (res.status === true) {
      handleSendMessage(
        notifications,
        "پیگیری دوره ای با موفقیت تغییر یافت",
        "success"
      );
      refetch();
    }
  };
  const handleTaskChangeClose = () => {
    setOpenChangeTask(false);
  };
  console.log("🚀 ~ rows:", rows);
  return (
    <Box>
      <ChangeStatus
        open={openChangeTask}
        handleClose={handleTaskChangeClose}
        refetch={refetch}
        FollowupData={rowInfo}
      />
      <DataGrid
        rows={rows || []}
        columns={columns}
        initialState={{
          sorting: {
            sortModel: [{ field: "dueOn", sort: "asc" }],
          },
        }}
        editMode="row"
        processRowUpdate={processRowUpdate}
      />
    </Box>
  );
}
